import { Listbox, Transition } from "@headlessui/react";
import Image from "next/image";
import { FC, memo, Fragment } from "react";

import CheckIcon from "/public/icon/select-check.svg";

interface Props {
  name?: string;
  defaultValue?: string | number;
  placeholder?: string;
  options: Array<{ text: string; value: string | number }>;
  className?: string;
  value?: string | number;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  isOptional?: boolean;
}

const SelectBase: FC<Props> = ({
  name,
  placeholder,
  options,
  className,
  defaultValue,
  isDisabled = false,
  value,
  onChange,
  isOptional,
}) => {
  const classNames = ["relative", className].join(" ");

  const selectedOptionText = (): string => {
    const selected = options.find((option) => option.value === value);
    if (null == selected) {
      return "";
    }
    return selected.text;
  };
  const selectedOptionValue = (): string | number => {
    const selected = options.find((option) => option.value === value);
    if (null == selected) {
      return "";
    }
    return selected.value;
  };
  return (
    <Listbox
      name={name}
      value={value || ""}
      onChange={(e: string) => {
        if (onChange === undefined || isDisabled) {
          return;
        }
        onChange(e);
      }}
      disabled={isDisabled}
    >
      <div className={classNames}>
        <Listbox.Button
          className={`relative py-2 pr-8 pl-2 w-full h-12 text-left placeholder:text-theme-light bg-white rounded border border-color-gray-200 focus:border-primary focus:outline-0 md:h-11 md:text-sm ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          disabled={isDisabled}
        >
          {null != value ? (
            <span key={value} className="block line-clamp-1">
              {selectedOptionText()}
            </span>
          ) : placeholder ? (
            <span
              className={`block truncate ${
                !isOptional ? "text-theme-light" : ""
              }`}
            >
              {placeholder}
            </span>
          ) : (
            <span
              className={`block truncate ${
                !isOptional ? "text-theme-light" : ""
              }`}
            >
              {defaultValue || "選択されていません"}
            </span>
          )}
          <span className="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none">
            <Image
              src="/customer/icon/triangle-pulldown.svg"
              width={24}
              height={24}
              alt=""
            />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="overflow-auto absolute z-10 py-1 mt-1 w-full max-h-60 text-base bg-white rounded-md focus:outline-none ring-1 ring-black/5 shadow-lg sm:text-sm">
            {isOptional && (
              <Listbox.Option
                value={undefined}
                disabled={isDisabled}
              >
                {({ active }) => (
                  <li
                    className={`flex items-center py-2 px-4 hover:bg-theme-hover cursor-pointer select-none ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    <CheckIcon className={`w-4 h-4 mr-2 invisible`} />
                    {placeholder}
                  </li>
                )}
              </Listbox.Option>
            )}
            {options.map((item, idx) => (
              <Listbox.Option
                key={idx}
                value={item.value}
                disabled={isDisabled}
              >
                {({ active }) => (
                  <li
                    className={`flex items-center py-2 px-4 hover:bg-theme-hover cursor-pointer select-none ${selectedOptionValue() === item.value
                      ? "bg-primary-thin"
                      : "bg-white"
                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    <CheckIcon
                      className={`w-4 h-4 mr-2 ${selectedOptionValue() === item.value
                        ? "visible"
                        : "invisible"
                        }`}
                    />
                    {item.text}
                  </li>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export const Select = memo(SelectBase);
